import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Users, ExternalLink, Music, Gift, Instagram, Youtube, Globe, Mail, Play, Heart, Share2, Copy, Check, Loader2, Ticket, UserPlus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { TierCards, CommunityFeed, FanLeaderboard } from '@/components/CommunityComponents';

const SOCIAL_ICONS = {
  instagram: Instagram, youtube: Youtube, website: Globe,
  spotify: Music, soundcloud: Music, tiktok: Play, twitter: Globe,
};

export default function CreatorHubPage() {
  const { handle } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/creators/hub/${handle}`).then(res => setHub(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [handle]);

  const handleFollow = async () => {
    if (!isAuthenticated) { toast.info('Sign in to follow'); return; }
    try {
      const res = await api.post(`/creators/hub/${hub.id}/follow`);
      setFollowing(res.data.status === 'followed');
      setHub(prev => ({ ...prev, followers_count: (prev.followers_count || 0) + (res.data.status === 'followed' ? 1 : -1) }));
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen bg-[#0f172a] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  if (!hub) return <div className="min-h-screen bg-[#0f172a] flex items-center justify-center text-white">Creator not found</div>;

  const accentColor = hub.accent_color || '#6366f1';
  const upcomingEvents = (hub.events || []).filter(e => new Date(e.date) >= new Date());
  const pastEvents = (hub.events || []).filter(e => new Date(e.date) < new Date());

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-50">
      {/* Cover Image */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        {hub.cover_image ? (
          <img src={hub.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${accentColor}33 0%, #0f172a 100%)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/50 to-transparent" />
      </div>

      {/* Profile Header */}
      <div className="max-w-4xl mx-auto px-4 -mt-24 relative z-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 mb-6">
          {/* Avatar */}
          <div className="w-32 h-32 rounded-2xl border-4 border-[#0f172a] overflow-hidden bg-slate-800 shrink-0">
            {hub.avatar ? (
              <img src={hub.avatar} alt={hub.stage_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl font-bold" style={{ color: accentColor }}>{hub.stage_name?.charAt(0) || '?'}</div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{hub.stage_name}</h1>
              {hub.is_verified && <Badge className="bg-indigo-600 text-white text-[10px]">Verified</Badge>}
            </div>
            {hub.tagline && <p className="text-lg text-slate-400 mt-1">{hub.tagline}</p>}
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
              {hub.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {hub.city}{hub.state ? `, ${hub.state}` : ''}</span>}
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {hub.followers_count || 0} followers</span>
              {hub.genres?.length > 0 && <span className="flex items-center gap-1"><Music className="w-3 h-3" /> {hub.genres.join(', ')}</span>}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button onClick={handleFollow} style={{ backgroundColor: following ? 'transparent' : accentColor, borderColor: accentColor }} className={following ? 'border text-white' : 'text-white'}>
              <UserPlus className="w-4 h-4 mr-1" /> {following ? 'Following' : 'Follow'}
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => { navigator.clipboard.writeText(window.location.href); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Link copied!'); }}>
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Bio */}
        {hub.bio && <p className="text-slate-400 mb-6 max-w-2xl">{hub.bio}</p>}

        {/* Social Links */}
        {Object.entries(hub.social_links || {}).some(([_, v]) => v) && (
          <div className="flex gap-3 mb-8">
            {Object.entries(hub.social_links || {}).map(([platform, url]) => {
              if (!url) return null;
              const Icon = SOCIAL_ICONS[platform] || Globe;
              return (
                <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors">
                  <Icon className="w-5 h-5 text-slate-400" />
                </a>
              );
            })}
            {hub.booking_email && (
              <a href={`mailto:${hub.booking_email}`} className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors">
                <Mail className="w-5 h-5 text-slate-400" />
              </a>
            )}
          </div>
        )}

        {/* Featured Media */}
        {hub.featured_media?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">Featured</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {hub.featured_media.map((media, i) => (
                <a key={i} href={media.url || media.embed_url} target="_blank" rel="noopener noreferrer">
                  <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all overflow-hidden group">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}33` }}>
                        <Play className="w-6 h-6" style={{ color: accentColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{media.title}</p>
                        <p className="text-xs text-slate-500 capitalize">{media.type}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-white" />
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Custom Links (Link-in-Bio) */}
        {hub.links?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">Links</h2>
            <div className="space-y-3">
              {hub.links.sort((a, b) => (a.order || 0) - (b.order || 0)).map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer">
                  <motion.div whileHover={{ scale: 1.02 }} className="block p-4 rounded-xl border border-slate-700 hover:border-indigo-500 bg-slate-800/50 text-center font-medium transition-colors">
                    {link.title}
                  </motion.div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5" style={{ color: accentColor }} /> Upcoming Events</h2>
            <div className="space-y-3">
              {upcomingEvents.map(event => (
                <Link key={event.id} to={`/events/${event.id}`}>
                  <Card className="bg-slate-800/50 border-slate-700 hover:border-indigo-500 transition-all">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-bold">{event.title}</p>
                        <p className="text-sm text-slate-400">{event.date} {event.time && `at ${event.time}`}</p>
                        {event.venue_name && <p className="text-xs text-slate-500">{event.venue_name}</p>}
                      </div>
                      <div className="text-right">
                        {event.price > 0 && <p className="font-bold" style={{ color: accentColor }}>${event.price}</p>}
                        <Button size="sm" style={{ backgroundColor: accentColor }} className="text-white mt-1"><Ticket className="w-3 h-3 mr-1" /> Tickets</Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Experiences */}
        {(hub.experiences || []).length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Gift className="w-5 h-5" style={{ color: accentColor }} /> Experiences</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {hub.experiences.map(exp => (
                <Link key={exp.id} to={`/experiences/${exp.id}`}>
                  <Card className="bg-slate-800/50 border-slate-700 hover:border-purple-500 transition-all overflow-hidden">
                    {exp.images?.[0] && <div className="aspect-video"><img src={exp.images[0]} alt="" className="w-full h-full object-cover" /></div>}
                    <CardContent className="p-4">
                      <p className="font-bold">{exp.title}</p>
                      {exp.hook && <p className="text-sm text-slate-400 italic mt-1">{exp.hook}</p>}
                      <p className="font-bold mt-2" style={{ color: accentColor }}>${exp.price}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Membership Tiers */}
        <TierCards hubId={hub.id} accentColor={accentColor} onSubscribe={async (tier) => {
          if (!isAuthenticated) { toast.info('Sign in to join'); return; }
          try {
            const res = await api.post('/community/subscribe', { hub_id: hub.id, tier_id: tier.id, handle: hub.handle, origin_url: window.location.origin });
            if (res.data.url) window.location.href = res.data.url;
            else { toast.success(`Joined ${tier.name}!`); setHub(prev => ({ ...prev, followers_count: (prev.followers_count || 0) + 1 })); }
          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        }} />

        {/* Community Feed */}
        <CommunityFeed hubId={hub.id} accentColor={accentColor} isCreator={user?.id === hub.user_id} />

        {/* Fan Leaderboard */}
        <FanLeaderboard hubId={hub.id} accentColor={accentColor} />

        {/* Booking CTA */}
        {(hub.booking_email || hub.booking_url) && (
          <Card className="mb-8 border-2" style={{ borderColor: accentColor }}>
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold mb-2">Book {hub.stage_name}</h3>
              <p className="text-slate-400 mb-4">Available for events, private parties, and venue residencies</p>
              <a href={hub.booking_url || `mailto:${hub.booking_email}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" style={{ backgroundColor: accentColor }} className="text-white"><Mail className="w-5 h-5 mr-2" /> Get in Touch</Button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center py-8 border-t border-slate-800">
          <Link to="/" className="text-slate-600 hover:text-slate-400 text-sm">
            Powered by EVENT<span className="text-indigo-500">★</span>GOERS
          </Link>
        </div>
      </div>
    </div>
  );
}
